import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./ChangeHistory.css";

interface FieldChange {
  fieldLabel: string;
  oldValue: string | null;
  newValue: string | null;
}

interface RelatedEntity {
  type: string;
  jobTitle?: string;
}

interface HistoryUser {
  name?: string;
  username?: string;
}

interface HistoryItem {
  id: number | string;
  timestamp: string;
  action: string;
  description: string;
  user: HistoryUser;
  note?: string;
  fieldChanges?: FieldChange[];
  relatedEntity?: RelatedEntity;
  entity_type?: string;
  entity_name?: string;
  changed_by?: string;
  changes?: string | Record<string, unknown>;
}

interface ChangeHistoryProps {
  history?: HistoryItem[];
  isLoading?: boolean;
  error?: Error | null;
}

type DateRangeFilter = "all" | "today" | "week" | "month";

const ChangeHistory: React.FC<ChangeHistoryProps> = ({
  history,
  isLoading,
  error,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [expandedItems, setExpandedItems] = useState<Set<number | string>>(
    new Set(),
  );
  const [filterAction, setFilterAction] = useState<string>("all");
  const [filterDateRange, setFilterDateRange] =
    useState<DateRangeFilter>("all");

  const toggleExpand = (id: number | string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const getActionColor = (action: string): string => {
    switch (action) {
      case "CREATE":
        return "#10b981"; // green
      case "UPDATE":
        return "#3b82f6"; // blue
      case "DELETE":
        return "#ef4444"; // red
      case "STAGE_CHANGE":
        return "#8b5cf6"; // purple
      case "STATUS_CHANGE":
        return "#f59e0b"; // amber
      default:
        return "#6b7280"; // gray
    }
  };

  const getActionIcon = (action: string): string => {
    switch (action) {
      case "CREATE":
        return "➕";
      case "UPDATE":
        return "✏️";
      case "DELETE":
        return "🗑️";
      case "STAGE_CHANGE":
        return "🔄";
      case "STATUS_CHANGE":
        return "🔀";
      default:
        return "📝";
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const groupByDate = (
    historyItems: HistoryItem[],
  ): Record<string, HistoryItem[]> => {
    const groups: Record<string, HistoryItem[]> = {};
    historyItems.forEach((item) => {
      const date = new Date(item.timestamp);
      const dateKey = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
    });
    return groups;
  };

  const filteredHistory = useMemo(() => {
    let filtered = history || [];

    if (filterAction !== "all") {
      filtered = filtered.filter((item) => item.action === filterAction);
    }

    if (filterDateRange !== "all") {
      const now = new Date();
      const cutoffDate = new Date();

      switch (filterDateRange) {
        case "today":
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case "week":
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case "month":
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }

      filtered = filtered.filter(
        (item) => new Date(item.timestamp) >= cutoffDate,
      );
    }

    return filtered;
  }, [history, filterAction, filterDateRange]);

  const groupedHistory = useMemo(
    () => groupByDate(filteredHistory),
    [filteredHistory],
  );

  if (isLoading) {
    return (
      <div className="change-history-loading">
        <div className="spinner"></div>
        <p>{t("common:loading.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="change-history-error">
        <p>
          {t("common:errors.failedToLoad")}:{" "}
          {error.message || t("common:errors.somethingWentWrong")}
        </p>
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="change-history-empty">
        <p>{t("common:empty.noData")}</p>
      </div>
    );
  }

  return (
    <div className="change-history">
      <div className="change-history-filters">
        <div className="filter-group">
          <label htmlFor="filter-action">{t("common:actions.filter")}:</label>
          <select
            id="filter-action"
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t("common:actions.viewAll")}</option>
            <option value="CREATE">{t("common:actions.create")}</option>
            <option value="UPDATE">{t("common:actions.update")}</option>
            <option value="DELETE">{t("common:actions.delete")}</option>
            <option value="STAGE_CHANGE">Stage Changes</option>
            <option value="STATUS_CHANGE">Status Changes</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="filter-date-range">{t("common:table.sortBy")}:</label>
          <select
            id="filter-date-range"
            value={filterDateRange}
            onChange={(e) =>
              setFilterDateRange(e.target.value as DateRangeFilter)
            }
            className="filter-select"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>
        </div>
      </div>

      <div className="change-history-timeline">
        {Object.entries(groupedHistory).map(([dateKey, items]) => (
          <div key={dateKey} className="history-date-group">
            <div className="history-date-header">
              <h3>{dateKey}</h3>
            </div>

            {items.map((item) => {
              const isExpanded = expandedItems.has(item.id);
              const hasFieldChanges =
                item.fieldChanges && item.fieldChanges.length > 0;

              return (
                <div
                  key={item.id}
                  className={`history-item history-item-${item.action.toLowerCase()}`}
                  style={{ borderLeftColor: getActionColor(item.action) }}
                >
                  <div className="history-item-header">
                    <div
                      className="history-item-icon"
                      style={{ backgroundColor: getActionColor(item.action) }}
                    >
                      {getActionIcon(item.action)}
                    </div>

                    <div className="history-item-content">
                      <div className="history-item-main">
                        <span className="history-item-description">
                          {item.description}
                        </span>
                        {item.relatedEntity && (
                          <span className="history-item-related">
                            {item.relatedEntity.type === "application" && (
                              <>
                                {" "}
                                for job:{" "}
                                <strong>{item.relatedEntity.jobTitle}</strong>
                              </>
                            )}
                          </span>
                        )}
                      </div>

                      <div className="history-item-meta">
                        <span className="history-item-user">
                          by{" "}
                          <strong>
                            {item.user.name || item.user.username}
                          </strong>
                        </span>
                        <span className="history-item-time">
                          {formatTimestamp(item.timestamp)}
                        </span>
                      </div>
                    </div>

                    {hasFieldChanges && (
                      <button
                        className="history-item-expand-btn"
                        onClick={() => toggleExpand(item.id)}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? "▼" : "▶"}
                      </button>
                    )}
                  </div>

                  {isExpanded && hasFieldChanges && (
                    <div className="history-item-details">
                      <div className="field-changes-list">
                        {item.fieldChanges?.map((change, idx) => (
                          <div
                            key={`${item.id}-field-${idx}-${change.fieldLabel}`}
                            className="field-change"
                          >
                            <div className="field-change-label">
                              {change.fieldLabel}:
                            </div>
                            <div className="field-change-values">
                              <span className="field-change-old">
                                {change.oldValue ?? <em>empty</em>}
                              </span>
                              <span className="field-change-arrow">→</span>
                              <span className="field-change-new">
                                {change.newValue ?? <em>empty</em>}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {item.note && (
                        <div className="history-item-note">
                          <strong>
                            {t("recruiting:jobs.detail.activity.addNote")}:
                          </strong>{" "}
                          {item.note}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChangeHistory;
